import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Download, Package, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order;

  if (!order) {
    navigate('/');
    return null;
  }

  const orderId = order.order_id || order.orderId;
  const isPending = order.status === 'pending_approval';
  const created = order.created_at || order.date;
  const total = Number(order.total || 0);
  const paymentMethod = (order.payment_method || order.paymentMethod || '').replace(/_/g, ' ');
  const items = order.items || [];
  const si = order.shipping_info || order.shippingInfo || {};
  const email = si.email || '';
  const firstName = si.firstName || si.first_name || '';
  const lastName = si.lastName || si.last_name || '';
  const zipCode = si.zip_code || si.zipCode || '';

  const downloadConfirmation = () => {
    const url = `${API}/orders/${orderId}/confirmation.pdf?email=${encodeURIComponent(email)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="order-confirmation-page">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className={`${isPending ? 'bg-yellow-100' : 'bg-green-100'} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4`}>
              {isPending ? <Clock className="h-12 w-12 text-yellow-600" /> : <CheckCircle className="h-12 w-12 text-green-600" />}
            </div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">{isPending ? 'Order Received — Awaiting Approval' : 'Order Confirmed!'}</h1>
            <p className="text-lg text-slate-600">
              {isPending
                ? "Your order is being reviewed by our team. We'll email you once it's approved."
                : "Thank you for your order. A confirmation email is on its way."}
            </p>
          </div>

          <Card className="mb-6 border-2">
            <CardHeader><CardTitle>Order Details</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-slate-600">Order Number:</span><span className="font-semibold text-slate-800" data-testid="order-id">{orderId}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Date:</span><span className="font-semibold text-slate-800">{created ? new Date(created).toLocaleDateString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Total:</span><span className="font-semibold text-blue-600 text-xl">${total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Payment Method:</span><span className="font-semibold text-slate-800 capitalize">{paymentMethod}</span></div>
                {isPending && <div className="flex justify-between"><span className="text-slate-600">Status:</span><span className="font-semibold text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded">Pending Approval</span></div>}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6 border-2">
            <CardHeader><CardTitle>Order Items</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {items.map((item, idx) => (
                  <div key={`${item.product_id || item.id || idx}`} className="flex gap-4 pb-4 border-b last:border-b-0 last:pb-0">
                    {item.image && <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg bg-slate-100" />}
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800">{item.name}</h3>
                      <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
                      <p className="text-sm text-slate-600">${Number(item.price).toFixed(2)} each</p>
                    </div>
                    <div className="text-right"><p className="font-semibold text-blue-600">${(Number(item.price) * Number(item.quantity)).toFixed(2)}</p></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6 border-2">
            <CardHeader><CardTitle>Shipping Address</CardTitle></CardHeader>
            <CardContent>
              <div className="text-slate-700">
                <p className="font-semibold">{firstName} {lastName}</p>
                {si.address && <p>{si.address}</p>}
                <p>{si.city}, {si.state} {zipCode}</p>
                {email && <p className="mt-2">{email}</p>}
                {si.phone && <p>{si.phone}</p>}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3 justify-center">
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={downloadConfirmation} data-testid="download-confirmation-pdf">
              <Download className="mr-2 h-4 w-4" /> Download Confirmation (PDF)
            </Button>
            <Link to="/products"><Button className="bg-blue-600 hover:bg-blue-700 text-white">Continue Shopping</Button></Link>
            <Link to="/track-order"><Button variant="outline"><Package className="mr-2 h-4 w-4" />Track Order</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
